import { ReactElement, useCallback } from 'react'
import Link from '@/components/link-component'
import RichText from '@/components/ui/rich-text'
import PageWrapper from '@/components/page-wrapper'
import ArticleBreadcrumb from '@/components/article-breadcrumb'
import SectionTabSection from '@/components/section/tab-section'
import SectionCta from '@/components/section/cta'
import { SetOfPricingComponent } from '@/components/section/set-of-pricing'
import PrintWrapper from '@/components/print-wrapper'
import SliderSection from '@/components/section/slider-section'
import Routes from '@/constants/routes'
import { getPdfPath, isPrint } from '@/utils/print'
import { TypeGooglePresentation } from '@/types/ctf'
import { usePage } from '@inertiajs/react'

type PresentationPostProps = {
  post: TypeGooglePresentation<'WITHOUT_UNRESOLVABLE_LINKS'>
  headerNav: any
  footerNav: any
  headerBanners: any
  newsletterForm: any
}

type WrapperProps = {
  children: ReactElement
}

export default function PresentationPost(props: Readonly<PresentationPostProps>) {
  const {
    fields: { title, url, speakerNotes, extraContent, bwPresentation },
  } = props.post

  const breadcrumb = [{ name: 'Resources', path: '/resources/' }, { name: title }]

  const isPrintView = isPrint()
  const pathname = usePage().url.split('?')[0]

  const Wrapper = useCallback(
    ({ children }: WrapperProps) =>
      isPrintView ? (
        <PrintWrapper
          title={title}
          section={'Resource Center'}
          pageUrl={Routes.hostname + pathname}
          orientation="landscape"
        >
          {children}
        </PrintWrapper>
      ) : (
        <PageWrapper
          breadcrumb={breadcrumb}
          seo={{ fields: { title } }}
          headerNav={props.headerNav}
          footerNav={props.footerNav}
          headerBanners={props.headerBanners}
          newsletterForm={props.newsletterForm}
        >
          <div className="max-w-8xl mx-auto px-5 lg:px-9 pt-10 min-h-[60vh] ">{children}</div>
        </PageWrapper>
      ),
    [isPrintView]
  )

  return (
    <Wrapper>
      <>
        {!isPrintView && (
          <div className="mb-12">
            <ArticleBreadcrumb breadcrumb={breadcrumb} />
          </div>
        )}
        {bwPresentation && (
          <SliderSection
            {...bwPresentation.fields}
            pdfPath={getPdfPath(pathname)}
            className="mx-auto text-indigoBlue"
          />
        )}
        {url && url.includes('docs.google.com/presentation') && (
          <div className=" max-w-4xl ">
            <div className="relative pt-[60.75%] mb-12" /* Player ratio: */>
              <iframe
                title={title}
                src={url}
                className="h-full w-full absolute top-0 left-0"
                allowFullScreen
                sandbox="allow-scripts allow-popups"
              />
            </div>
          </div>
        )}
        {!bwPresentation && (
          <>
            <h1 className="h2">{title}</h1>
            {speakerNotes && <RichText body={speakerNotes} />}
          </>
        )}
        {!isPrintView && (
          <Link to="/resources/">
            <div className="inline-flex items-center my-12">
              <span className="font-bwi">&#xe96b;</span> Back to Resources
            </div>
          </Link>
        )}

        {extraContent && (
          <div className="my-16">
            {extraContent.map((section) => {
              if (!section) return null
              const contentType = section.sys.contentType?.sys.id
              if (contentType === 'sectionSetOfPricing') {
                return <SetOfPricingComponent key={section.sys.id} {...section.fields} />
              }

              if (contentType === 'sectionCta') return <SectionCta key={section.sys.id} {...(section.fields as any)} />

              if (contentType === 'sectionTabSection')
                return <SectionTabSection key={section.sys.id} {...(section.fields as any)} />
              return null
            })}
          </div>
        )}
      </>
    </Wrapper>
  )
}
